import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ChatMessage, ChatQueryResponse } from "../services/model/ChatModels";
import { useChatQueryApiService } from "../services/api/AIInsightsApiService";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";

interface AIInsightsChatContextType {
  isOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  conversationHistory: ChatMessage[];
  sendMessage: (query: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  resetConversation: () => void;
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  lastResponse: ChatQueryResponse | null;
}

export const AIInsightsChatContext = createContext<
  AIInsightsChatContextType | undefined
>(undefined);

interface AIInsightsChatProviderProps {
  children: React.ReactNode;
}

export const AIInsightsChatProvider: React.FC<AIInsightsChatProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>(
    []
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<ChatQueryResponse | null>(
    null
  );
  const [isSending, setIsSending] = useState(false);

  const auth = useContext(AuthContext);
  const chatQueryMutation = useChatQueryApiService();

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetConversation = useCallback(() => {
    setConversationHistory([]);
    setSelectedCategory(null);
    setError(null);
    setLastResponse(null);
  }, []);

  const sendMessage = useCallback(
    async (query: string) => {
      if (!query.trim() || isSending) return;

      // Generate unique request ID
      const requestId = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // Build user message with requestId
      const userMessage: ChatMessage = {
        role: "user",
        content: query.trim(),
        timestamp: Date.now(),
        requestId,
      };

      // Build history with the new user message synchronously
      const historyWithUser = [...conversationHistory, userMessage];
      setConversationHistory(historyWithUser);

      setError(null);
      setIsSending(true);

      try {
        // Get employer_id from auth context
        const employerId = auth?.employer?.id;

        // Send query to AI service with the updated history
        const response = await chatQueryMutation.mutateAsync({
          query: query.trim(),
          employer_id: employerId,
          conversation_history: historyWithUser,
          include_data: true,
        });

        // Add assistant response to conversation with matching requestId
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: response.response,
          timestamp: Date.now(),
          requestId, // Match with the user message
        };

        setConversationHistory((prev) => [...prev, assistantMessage]);
        setLastResponse(response);
      } catch (err: any) {
        const errorMessage =
          HttpClientError(err) || "Failed to get response. Please try again.";
        setError(errorMessage);

        // Remove only the specific user message by requestId, not blindly slice
        setConversationHistory((prev) =>
          prev.filter((msg) => msg.requestId !== requestId)
        );
      } finally {
        setIsSending(false);
      }
    },
    [conversationHistory, isSending, auth, chatQueryMutation]
  );

  const contextValue = useMemo<AIInsightsChatContextType>(
    () => ({
      isOpen,
      openChat,
      closeChat,
      conversationHistory,
      sendMessage,
      isLoading: chatQueryMutation.isLoading || isSending,
      error,
      clearError,
      resetConversation,
      selectedCategory,
      setSelectedCategory,
      lastResponse,
    }),
    [
      isOpen,
      openChat,
      closeChat,
      conversationHistory,
      sendMessage,
      chatQueryMutation.isLoading,
      isSending,
      error,
      clearError,
      resetConversation,
      selectedCategory,
      lastResponse,
    ]
  );

  return (
    <AIInsightsChatContext.Provider value={contextValue}>
      {children}
    </AIInsightsChatContext.Provider>
  );
};

export const useAIInsightsChat = (): AIInsightsChatContextType => {
  const context = useContext(AIInsightsChatContext);
  if (!context) {
    throw new Error(
      "useAIInsightsChat must be used within AIInsightsChatProvider"
    );
  }
  return context;
};
